/**
 * Workspace Context
 * 
 * Manages current workspace and connector selection.
 * 
 * NEW MODEL (1 property → 1 workspace):
 * - Each property belongs to exactly ONE workspace
 * - Properties can be enabled/disabled within the workspace
 * - Default selection is the first ENABLED property
 */

import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import type { ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./AuthContext";
import { useURLState } from "@/hooks/useURLState";
import type { Workspace, Connector, WorkspacePropertyInfo } from "@/lib/types/workspace";
import { getWorkspace as getWorkspaceAPI } from "@/lib/api/workspaces";
import { getWorkspaceProperties, updateWorkspaceProperty } from "@/lib/api/workspace-team";
import { toast } from "sonner";

interface WorkspaceContextType {
  workspace: Workspace | null;
  isLoading: boolean;
  error: Error | null;
  connectors: Connector[];
  activeConnectors: Connector[];
  selectedConnectorIds: string[];
  toggleConnector: (connectorId: string) => void;
  selectAllConnectors: () => void;
  clearConnectors: () => void;
  refetch: () => void;
  // Helper to check if Humblytics tracking is available
  hasHumblyticsTracking: boolean;
  // Property/site selection within workspace (for analytics scoping)
  propertyId?: string;
  // Single selected property (first of selectedPropertyIds, or null)
  selectedPropertyId: string | null;
  setSelectedPropertyId: (propertyId: string | null) => void;
  // Multi-select: all currently selected property IDs
  selectedPropertyIds: string[];
  togglePropertySelection: (propertyId: string) => void;
  // Full property info with enabled status
  properties: WorkspacePropertyInfo[];
  // Enabled properties only
  enabledProperties: WorkspacePropertyInfo[];
  // Toggle property enabled/disabled state
  togglePropertyEnabled: (propertyId: string, enabled: boolean) => Promise<void>;
  // Whether aggregated view is selected (>1 properties selected)
  isAggregateMode: boolean;
  // Property IDs used for aggregate API calls (the selected ones, capped at 5)
  aggregatePropertyIds: string[];
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(
  undefined
);

/**
 * Fetch workspace by ID or slug-ID, including its properties
 */
async function fetchWorkspaceWithProperties(
  workspaceId: string | null,
  token: string
): Promise<Workspace | null> {
  if (!workspaceId || !token) return null;
  
  // Special case: 'new' is not a workspace ID
  if (workspaceId === 'new') return null;

  try {
    // Fetch workspace (already enriched with properties in the API layer)
    const workspace = await getWorkspaceAPI(workspaceId, token);
    
    if (!workspace) return null;

    // If workspace doesn't have properties, try fetching them separately
    if (!workspace.properties || workspace.properties.length === 0) {
      try {
        const propertiesResponse = await getWorkspaceProperties(workspace.workspaceId, token);
        if (propertiesResponse.properties && propertiesResponse.properties.length > 0) {
          workspace.properties = propertiesResponse.properties.filter((p: any) => !p.archived).map((p): WorkspacePropertyInfo => ({
            propertyId: p.property_id ?? (p as any).propertyId,
            name: p.name,
            domain: p.domain,
            enabled: p.enabled ?? true,
            trackingStatus: p.tracking_status ?? (p as any).trackingStatus ?? 'not_installed',
            isStaging: (p as any).is_staging ?? (p as any).isStaging ?? false,
            subscription: (p as any).subscription,
            lastActivatedAt: (p as any).lastActivatedAt,
          }));
        }
      } catch {
        // Properties fetch failed, continue with empty properties
      }
    }

    return workspace;
  } catch (error) {
    console.error('Error fetching workspace:', error);
    return null;
  }
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { workspaceId: workspaceSlugId } = useParams<{ workspaceId: string }>();
  const { token } = useAuth();
  const { state, updateState } = useURLState();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // ?site= supports comma-separated IDs for multi-select: ?site=id1,id2,id3
  const siteParam = searchParams.get('site') || '';
  const selectedPropertyIds = useMemo(
    () => siteParam.split(',').map(s => s.trim()).filter(Boolean),
    [siteParam]
  );
  const selectedPropertyId = selectedPropertyIds[0] || null;

  // Track previous workspaceSlugId to detect actual workspace changes (not initial mount)
  const prevWorkspaceSlugIdRef = useRef<string | undefined | null>(null);

  // Extract workspace ID from slug (using same pattern as property)
  // Handle special routes like 'new' that aren't actual workspace IDs
  const workspaceId = useMemo(() => {
    if (!workspaceSlugId) return null;
    // Special routes that aren't workspace IDs
    if (workspaceSlugId === 'new') {
      return null; // Don't try to fetch a workspace for 'new'
    }
    // For workspace routes, the slug-ID format is: {name-slug}-{workspaceId}
    // But workspaceId itself can contain dashes (e.g., "workspace-humblytics.com-0")
    // So we pass the full slug to getWorkspace and let it handle the matching
    // This allows getWorkspace to try multiple matching strategies
    return workspaceSlugId;
  }, [workspaceSlugId]);

  // Fetch workspace data with properties
  const {
    data: workspace,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => fetchWorkspaceWithProperties(workspaceId!, token!),
    enabled: !!workspaceId && !!token,
    staleTime: 0,
  });

  const connectors = workspace?.connectors || [];
  const properties: WorkspacePropertyInfo[] = workspace?.properties || [];
  
  const enabledProperties = useMemo(
    () => properties.filter(p => p.enabled),
    [properties]
  );

  // Mutation for toggling property enabled state
  const togglePropertyMutation = useMutation({
    mutationFn: async ({ propertyId, enabled }: { propertyId: string; enabled: boolean }) => {
      if (!workspace?.workspaceId || !token) {
        throw new Error("Missing workspace or auth");
      }
      return updateWorkspaceProperty(workspace.workspaceId, propertyId, { enabled }, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspace", workspaceId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update property");
    },
  });

  // Invalidate queries when workspace actually changes (not on initial mount or re-renders)
  // Navigation to a new workspace never includes ?site=, so no URL cleanup is needed here
  useEffect(() => {
    if (prevWorkspaceSlugIdRef.current === null) {
      // First mount — record slug, don't invalidate
      prevWorkspaceSlugIdRef.current = workspaceSlugId;
      return;
    }
    if (prevWorkspaceSlugIdRef.current === workspaceSlugId) {
      // Same workspace (re-render or Strict Mode second call) — do nothing
      return;
    }
    // Workspace changed — invalidate stale data
    prevWorkspaceSlugIdRef.current = workspaceSlugId;
    queryClient.cancelQueries();
    queryClient.invalidateQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceSlugId]);

  // Handle property selection logic
  // - No site selected: Auto-select the first enabled property (aggregate is opt-in only)
  // - Invalid selection: Fall back to first enabled property
  // NOTE: Skip while workspace is loading to avoid stripping ?site= before properties are fetched
  useEffect(() => {
    if (isLoading) return;
    // Always default to first enabled property — aggregate mode must be manually enabled
    const firstWithId = enabledProperties.find(p => p.propertyId);
    if (selectedPropertyIds.length === 0 && firstWithId) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('site', firstWithId.propertyId);
        return next;
      }, { replace: true });
    }
    // If all selected properties are invalid/disabled, fall back to first enabled
    if (selectedPropertyIds.length > 0 && properties.length > 0) {
      const validIds = selectedPropertyIds.filter(id => {
        const p = properties.find(p => p.propertyId === id);
        return p && p.enabled;
      });
      if (validIds.length === 0 && firstWithId) {
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          next.set('site', firstWithId.propertyId);
          return next;
        }, { replace: true });
      } else if (validIds.length < selectedPropertyIds.length) {
        // Remove invalid IDs from selection
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          next.set('site', validIds.join(','));
          return next;
        }, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, properties, enabledProperties, siteParam]);

  // Get selected connectors from URL, default to all active if none selected
  const selectedConnectorIds = useMemo(() => {
    if (state.sources.length > 0) {
      // Map legacy source IDs to connector IDs
      return state.sources;
    }
    // Default to all connected connectors
    return connectors
      .filter((c) => c.status === 'connected')
      .map((c) => c.connectorId);
  }, [state.sources, connectors]);

  const activeConnectors = connectors.filter((c) =>
    selectedConnectorIds.includes(c.connectorId)
  );

  const toggleConnector = useCallback(
    (connectorId: string) => {
      const newConnectors = selectedConnectorIds.includes(connectorId)
        ? selectedConnectorIds.filter((id) => id !== connectorId)
        : [...selectedConnectorIds, connectorId];

      updateState({ sources: newConnectors });
    },
    [selectedConnectorIds, updateState]
  );

  const selectAllConnectors = useCallback(() => {
    const allIds = connectors
      .filter((c) => c.status === 'connected')
      .map((c) => c.connectorId);
    updateState({ sources: allIds });
  }, [connectors, updateState]);

  const clearConnectors = useCallback(() => {
    updateState({ sources: [] });
  }, [updateState]);

  // Check if any connector is Humblytics tracking
  const hasHumblyticsTracking = useMemo(() => {
    return activeConnectors.some((c) => c.type === 'humblytics');
  }, [activeConnectors]);

  // Aggregate mode = more than one property selected
  const isAggregateMode = selectedPropertyIds.length > 1;

  // Aggregate API calls use the explicitly selected IDs, capped at 5
  const aggregatePropertyIds = useMemo(() =>
    selectedPropertyIds.slice(0, 5),
    [selectedPropertyIds]
  );

  // Get active propertyId for analytics scoping
  // Priority: 1) User-selected property, 2) First enabled property, 3) Connector metadata fallback
  const propertyId = useMemo(() => {
    // If user has selected a specific property, use that
    if (selectedPropertyId) {
      return selectedPropertyId;
    }
    
    // Use first enabled property from workspace that has a valid ID
    const firstWithId = enabledProperties.find(p => p.propertyId);
    if (firstWithId) {
      return firstWithId.propertyId;
    }
    
    // Fallback: check connector metadata for legacy compatibility
    const humblyticsConnector = activeConnectors.find(
      (c) => c.type === 'humblytics'
    );
    if (humblyticsConnector) {
      if (humblyticsConnector.metadata?.propertyIds?.length) {
        return humblyticsConnector.metadata.propertyIds[0];
      }
      return humblyticsConnector.propertyId || humblyticsConnector.metadata?.propertyId;
    }
    
    return workspace?.connectors[0]?.propertyId || workspace?.connectors[0]?.metadata?.propertyId;
  }, [selectedPropertyId, enabledProperties, activeConnectors, workspace]);

  const setSelectedPropertyId = useCallback((propertyId: string | null) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (propertyId) {
        next.set('site', propertyId);
      } else {
        next.delete('site');
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Toggle a property in/out of the multi-select
  const togglePropertySelection = useCallback((propertyId: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      const current = (next.get('site') || '').split(',').filter(Boolean);
      const idx = current.indexOf(propertyId);
      let updated: string[];
      if (idx >= 0) {
        // Deselect — but always keep at least one selected
        updated = current.filter(id => id !== propertyId);
        if (updated.length === 0) updated = current; // prevent empty
      } else {
        updated = [...current, propertyId].slice(0, 5); // cap at 5
      }
      next.set('site', updated.join(','));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const togglePropertyEnabled = useCallback(async (propertyId: string, enabled: boolean) => {
    await togglePropertyMutation.mutateAsync({ propertyId, enabled });
  }, [togglePropertyMutation]);

  return (
    <WorkspaceContext.Provider
      value={{
        workspace: workspace || null,
        isLoading,
        error: error as Error | null,
        connectors,
        activeConnectors,
        selectedConnectorIds,
        toggleConnector,
        selectAllConnectors,
        clearConnectors,
        refetch,
        hasHumblyticsTracking,
        propertyId,
        selectedPropertyId,
        setSelectedPropertyId,
        selectedPropertyIds,
        togglePropertySelection,
        properties,
        enabledProperties,
        togglePropertyEnabled,
        isAggregateMode,
        aggregatePropertyIds,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within WorkspaceProvider");
  }
  return context;
}

// Safe hook that returns null if provider is not available
export function useWorkspaceSafe() {
  return useContext(WorkspaceContext);
}

